// src/components/CreateAgent.js — « Employés » (/create-agent; the old
// /employee-productivity redirects here). Two views: « Équipe » (the members,
// their load and outcomes from ONE call, GET /api/employees/roster-stats, and
// the member documents from GET /api/employees/agents) and « Productivité »
// (EmployeeProductivity.js, one member at a time).
// Writes: POST /api/employees/create-agent, PUT /api/employees/update-employee,
// DELETE /api/employees/delete-employee/{email} (behind a confirmation).
// URL: ?view=productivity&employee=<id> (the row action « Productivité »).
// A team member (role employee) sees « Ma productivité » only: their own
// numbers, no member list, no tab (Kevin, 29/10).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChartLine, ChevronRight, CircleCheck, Inbox, Pencil, Smile, TriangleAlert, UserMinus, UserPlus, Users,
} from 'lucide-react';
import {
  Badge, Banner, Card, ConfirmDialog, EmptyState, Field, Kpi, Modal, OverflowMenu, PageHeader, Segmented, Tabs, Toast,
  initialsOf,
} from '../design/ui';
import EmployeeProductivity from './EmployeeProductivity';
import { buildCreatePayload, buildUpdatePayload, formatNumber, moodTone, showsExternalId, teamAttention } from './employeesModel';
import { sessionRole } from './accountScope';
import './CreateAgent.css';

const MANAGE_ROLES = ['admin', 'e-commerce', 'experience'];
const PRODUCTIVITY_ROLES = ['admin', 'seller', 'logistics', 'logistician', 'eshop', 'employee'];
const ROSTER_WINDOWS = [7, 30, 90];

const dummyAgents = [
  {
    _id: 'exp_agent_1',
    name: 'Sarah Johnson',
    email: 'sarah.j@example.com',
    description: 'Senior support specialist with expertise in product returns and customer satisfaction.',
  },
  {
    _id: 'exp_agent_2',
    name: 'Michael Chen',
    email: 'michael.c@example.com',
    description: 'Technical support expert specializing in order processing and shipping issues.',
  },
  {
    _id: 'exp_agent_3',
    name: 'Emma Rodriguez',
    email: 'emma.r@example.com',
    description: 'Customer experience specialist focused on product inquiries and feedback management.',
  },
];

// Demo roster for the "experience" role (same shape as /api/employees/roster-stats)
const dummyRoster = {
  window_days: 7,
  stale_after_days: 3,
  employees: [
    { employee_id: 'exp_agent_1', name: 'Sarah Johnson', email: 'sarah.j@example.com', description: dummyAgents[0].description, open_assigned: 12, stale_open: 2, resolved_window: 18, resolution_rate_window: 92, avg_sentiment_window: 58, ai_validated_share_window: 71, sentiment_n: 14 },
    { employee_id: 'exp_agent_2', name: 'Michael Chen', email: 'michael.c@example.com', description: dummyAgents[1].description, open_assigned: 8, stale_open: 0, resolved_window: 11, resolution_rate_window: 88, avg_sentiment_window: 55, ai_validated_share_window: 64, sentiment_n: 9 },
    { employee_id: 'exp_agent_3', name: 'Emma Rodriguez', email: 'emma.r@example.com', description: dummyAgents[2].description, open_assigned: 5, stale_open: 1, resolved_window: 9, resolution_rate_window: 95, avg_sentiment_window: 62, ai_validated_share_window: 80, sentiment_n: 7 },
  ],
  team: { employees: 3, open_assigned: 25, stale_open: 3, resolved_window: 38, resolution_rate_window: 91, avg_sentiment_window: 58 },
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const emptyForm = { name: '', email: '', description: '', externalAdminId: '' };

const CreateAgent = () => {
  const { t } = useTranslation();
  const tp = (key, one, other, options) => t(key, options.count === 1 ? one : other, options);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userRole = useMemo(() => sessionRole(), []);
  const isSelf = userRole === 'employee';
  const canManage = MANAGE_ROLES.includes(userRole);
  const canViewProductivity = PRODUCTIVITY_ROLES.includes(userRole);
  const requestedView = searchParams.get('view') === 'productivity' ? 'productivity' : 'team';
  const view = !canManage ? 'productivity' : (canViewProductivity ? requestedView : 'team');
  const productivityEmployeeId = searchParams.get('employee') || null;

  const [user, setUser] = useState(undefined);
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(7);
  const [roster, setRoster] = useState(undefined); // undefined = loading, null = unavailable
  const [form, setForm] = useState(null); // { mode: 'create' | 'edit', agent?, ...fields }
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [toRemove, setToRemove] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!toast || toast.tone === 'danger') return undefined;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  const updateParams = useCallback((updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') next.delete(key);
        else next.set(key, String(value));
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const fetchAgents = useCallback(async () => {
    try {
      if (userRole === 'experience') {
        setAgents(dummyAgents);
        return;
      }
      const response = await axios.get('/api/employees/agents', { headers: authHeaders() });
      setAgents(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch agents:', error);
    } finally {
      setIsLoading(false);
    }
  }, [userRole]);

  const fetchRoster = useCallback(async (days) => {
    if (userRole === 'experience') {
      setRoster({ ...dummyRoster, window_days: days });
      return;
    }
    try {
      const response = await axios.get(`/api/employees/roster-stats?window_days=${days}`, { headers: authHeaders() });
      setRoster(response.data);
    } catch (error) {
      console.error('Failed to fetch roster stats:', error);
      setRoster(null);
    }
  }, [userRole]);

  useEffect(() => {
    if (!localStorage.getItem('access_token')) {
      navigate('/');
      return;
    }
    // Roles without team management (productivity-only view) skip the
    // admin-scoped agents/user fetches entirely.
    if (!canManage) {
      setIsLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        const response = await axios.get('/api/users/user', { headers: authHeaders() });
        setUser(response.data);
      } catch (error) {
        console.error('Failed to fetch user data:', error);
        setUser(null);
      }
      await fetchAgents();
    };
    fetchData();
  }, [navigate, canManage, fetchAgents]);

  useEffect(() => {
    if (!canManage || !localStorage.getItem('access_token')) return;
    setRoster(undefined);
    fetchRoster(windowDays);
  }, [canManage, windowDays, fetchRoster]);

  const refreshTeam = () => {
    fetchAgents();
    fetchRoster(windowDays);
  };

  const openCreate = () => {
    setForm({ mode: 'create', ...emptyForm });
    setFormError('');
  };

  const openEdit = (agent) => {
    setForm({
      mode: 'edit',
      agent,
      name: agent.name || '',
      email: agent.email || '',
      description: agent.description || '',
      externalAdminId: agent.external_admin_id || '',
    });
    setFormError('');
  };

  const saveErrorText = (error, fallback) => {
    const detail = error?.response?.data?.detail;
    if (detail === 'Email already registered' || detail === 'New email already registered') {
      return t('teamStatsUi.team.emailTaken', 'Cette adresse e-mail est déjà utilisée par un membre.');
    }
    return fallback;
  };

  const submitForm = async (event) => {
    event.preventDefault();
    if (!form) return;
    setSaving(true);
    setFormError('');
    try {
      if (form.mode === 'create') {
        await axios.post('/api/employees/create-agent', buildCreatePayload(form), { headers: authHeaders() });
        setToast({ tone: 'success', text: t('teamStatsUi.team.created', '{{name}} est ajouté à l’équipe : ses identifiants lui sont envoyés par e-mail.', { name: form.name }) });
      } else {
        await axios.put('/api/employees/update-employee', buildUpdatePayload(form.agent, form), { headers: authHeaders() });
        setToast({ tone: 'success', text: t('teamStatsUi.team.updated', 'Fiche de {{name}} enregistrée.', { name: form.name }) });
      }
      setForm(null);
      refreshTeam();
    } catch (error) {
      console.error('Failed to save employee:', error);
      setFormError(saveErrorText(error, form.mode === 'create'
        ? t('teamStatsUi.team.createFailed', 'Le membre n’a pas pu être ajouté. Réessayez.')
        : t('teamStatsUi.team.updateFailed', 'La fiche n’a pas pu être enregistrée. Réessayez.')));
    } finally {
      setSaving(false);
    }
  };

  const confirmRemove = async () => {
    if (!toRemove) return;
    setRemoving(true);
    try {
      await axios.delete(`/api/employees/delete-employee/${toRemove.email}`, { headers: authHeaders() });
      setToast({ tone: 'success', text: t('teamStatsUi.team.removed', '{{name}} ne fait plus partie de l’équipe.', { name: toRemove.name || toRemove.email }) });
      setToRemove(null);
      refreshTeam();
    } catch (error) {
      console.error('Failed to delete agent:', error);
      setToRemove(null);
      setToast({ tone: 'danger', text: t('teamStatsUi.team.removeFailed', '{{name}} n’a pas pu être retiré. Réessayez.', { name: toRemove.name || toRemove.email }) });
    } finally {
      setRemoving(false);
    }
  };

  const openProductivityFor = (employeeId) => updateParams({ view: 'productivity', employee: employeeId });

  // Roster rows are keyed by employee id; the agent documents stay the
  // source of identity so the edit form always has the full document.
  const rosterByEmployee = useMemo(() => {
    const map = {};
    (roster?.employees || []).forEach((row) => { map[row.employee_id] = row; });
    return map;
  }, [roster]);
  const attention = useMemo(() => teamAttention(roster), [roster]);
  const staleDays = roster?.stale_after_days ?? 3;

  const team = roster?.team;
  const subtitle = canManage && !isLoading && agents.length === 0
    ? t('teamStatsUi.team.subtitleEmpty', 'Aucun membre pour l’instant : vous traitez les tickets seul avec l’agent IA')
    : canManage && !isLoading
    ? [
      tp('teamStatsUi.team.subtitleMembers', '{{count}} membre', '{{count}} membres', { count: agents.length }),
      team ? tp('teamStatsUi.team.subtitleOpen', '{{count}} ticket ouvert assigné', '{{count}} tickets ouverts assignés', { count: team.open_assigned }) : null,
    ].filter(Boolean).join(' · ')
    : t('teamStatsUi.team.subtitleSelf', 'Votre activité sur les tickets');

  const header = (
    <PageHeader
      title={isSelf ? t('accountScopeUi.nav.myProductivity', 'Ma productivité') : t('employeesPage.navLabel', 'Employés')}
      subtitle={subtitle}
      actions={canManage && view === 'team' && user?.subscription_plan !== 'free' ? (
        <button type="button" className="u-btn u-btn--primary" onClick={openCreate}>
          <UserPlus aria-hidden="true" />
          {t('teamStatsUi.team.add', 'Ajouter un membre')}
        </button>
      ) : null}
    />
  );

  if (canManage && (user === undefined || isLoading)) {
    return (
      <div className="u-page team-v2" aria-busy="true">
        {header}
        <div className="u-well">
          <div className="u-kpi-row" aria-hidden="true">
            {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton team-v2__kpi-skeleton" />)}
          </div>
          <div className="u-card team-v2__table-skeleton" aria-hidden="true">
            {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton" />)}
          </div>
          <span className="team-v2__sr">{t('teamStatsUi.team.loading', 'Chargement de l’équipe…')}</span>
        </div>
      </div>
    );
  }

  if (canManage && user?.subscription_plan === 'free') {
    return (
      <div className="u-page team-v2">
        {header}
        <div className="u-well">
          <Card>
            <EmptyState
              icon={Users}
              title={t('teamStatsUi.team.freeTitle', 'L’équipe n’est pas incluse dans la formule gratuite')}
              action={(
                <Link className="u-btn u-btn--primary" to="/billing">
                  {t('teamStatsUi.team.upgrade', 'Voir les formules')}
                </Link>
              )}
            >
              {t('teamStatsUi.team.freeHint', 'Passez à une formule payante pour ajouter des membres et suivre leur productivité.')}
            </EmptyState>
          </Card>
        </div>
      </div>
    );
  }

  const renderAttention = () => {
    if (!attention.length) return null;
    return (
      <Card
        icon={TriangleAlert}
        iconTone="warning"
        title={t('teamStatsUi.attention', 'À vérifier')}
        badge={<Badge tone="warning">{attention.length}</Badge>}
        flush
        className="team-v2__attention"
      >
        <ul className="u-list">
          {attention.map((item) => (
            <li className="u-row" key={item.key}>
              <Badge tone="warning" dot>{t('teamStatsUi.watch', 'À surveiller')}</Badge>
              <span className="u-avatar u-avatar--sm" aria-hidden="true">{initialsOf(item.name)}</span>
              <div className="u-row__main">
                <div className="u-row__title">
                  {tp('teamStatsUi.team.staleTitle', '{{name}} : {{count}} ticket ouvert sans activité depuis plus de {{days}} jours', '{{name}} : {{count}} tickets ouverts sans activité depuis plus de {{days}} jours', { name: item.name, count: item.count, days: staleDays })}
                </div>
                <div className="u-row__meta u-row__meta--wrap">
                  {tp('teamStatsUi.team.staleMeta', 'Sur {{count}} ticket ouvert qui lui est assigné.', 'Sur {{count}} tickets ouverts qui lui sont assignés.', { count: item.open })}
                </div>
              </div>
              <div className="u-row__side">
                {canViewProductivity && (
                  <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => openProductivityFor(item.employeeId)}>
                    {t('teamStatsUi.team.seeQueue', 'Voir sa file')}
                    <ChevronRight aria-hidden="true" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </Card>
    );
  };

  const renderKpis = () => {
    if (roster === undefined) {
      return (
        <div className="u-kpi-row" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton team-v2__kpi-skeleton" />)}
        </div>
      );
    }
    if (!team) return null;
    const busy = (roster.employees || []).filter((row) => row.open_assigned > 0).length;
    return (
      <div className="u-kpi-row">
        <Kpi
          icon={Users}
          label={t('teamStatsUi.team.kpiMembers', 'Membres')}
          value={formatNumber(team.employees)}
          context={tp('teamStatsUi.team.kpiMembersContext', '{{count}} avec des tickets ouverts', '{{count}} avec des tickets ouverts', { count: busy })}
        />
        <Kpi
          icon={team.stale_open > 0 ? TriangleAlert : Inbox}
          tone={team.stale_open > 0 ? 'warning' : undefined}
          label={t('createAgent.sumOpen', 'Ouverts assignés')}
          value={formatNumber(team.open_assigned)}
          context={team.stale_open > 0
            ? t('createAgent.sumStale', 'dont {{count}} sans activité > {{days}} j', { count: team.stale_open, days: staleDays })
            : t('teamStatsUi.team.kpiOpenFresh', 'tous suivis depuis moins de {{days}} jours', { days: staleDays })}
          contextTone={team.stale_open > 0 ? 'warning' : undefined}
        />
        <Kpi
          icon={CircleCheck}
          tone="success"
          label={t('teamStatsUi.team.kpiResolved', 'Résolus · {{days}} j', { days: windowDays })}
          value={formatNumber(team.resolved_window)}
          context={team.resolution_rate_window != null
            ? t('teamStatsUi.team.kpiResolvedContext', '{{rate}} % des tickets reçus en {{days}} j sont résolus', { rate: team.resolution_rate_window, days: windowDays })
            : t('teamStatsUi.team.kpiResolvedNone', 'aucun ticket reçu en {{days}} j', { days: windowDays })}
          meter={team.resolution_rate_window != null ? { value: team.resolution_rate_window, max: 100, tone: 'success', label: t('teamStatsUi.team.kpiResolved', 'Résolus · {{days}} j', { days: windowDays }) } : undefined}
        />
        {team.avg_sentiment_window != null && (
          <Kpi
            icon={Smile}
            tone={moodTone(team.avg_sentiment_window)}
            label={t('createAgent.sumMood', 'Humeur clients · {{days}} j', { days: windowDays })}
            value={Math.round(team.avg_sentiment_window)}
            unit="/100"
            context={t('teamStatsUi.team.kpiMoodContext', 'sentiment moyen sur leurs tickets')}
          />
        )}
      </div>
    );
  };

  const renderTable = () => {
    if (!agents.length) {
      return (
        <Card>
          <EmptyState
            icon={Users}
            title={t('teamStatsUi.team.emptyTitle', 'Aucun membre dans l’équipe')}
            action={(
              <button type="button" className="u-btn u-btn--primary" onClick={openCreate}>
                <UserPlus aria-hidden="true" />
                {t('teamStatsUi.team.add', 'Ajouter un membre')}
              </button>
            )}
          >
            {t('teamStatsUi.team.emptyHint', 'Ajoutez les personnes qui traitent les tickets avec vous : l’agent IA peut leur assigner les tickets qu’il ne règle pas seul.')}
          </EmptyState>
        </Card>
      );
    }
    const hasStats = roster !== undefined && roster !== null;
    return (
      <Card
        icon={Users}
        title={t('teamStatsUi.team.membersTitle', 'Membres')}
        badge={<Badge>{formatNumber(agents.length)}</Badge>}
        description={t('teamStatsUi.team.membersDesc', 'Chacun se connecte avec son e-mail. L’agent IA lit le rôle de chacun pour choisir à qui assigner un ticket.')}
        flush
      >
        <div className="u-table-scroll">
          <table className="u-table team-v2__table">
            <thead>
              <tr>
                <th>{t('teamStatsUi.team.colMember', 'Membre')}</th>
                <th style={{ width: 176 }}>{t('createAgent.colRole', 'Rôle')}</th>
                <th style={{ width: 196 }}>{t('createAgent.colLoad', 'Charge actuelle')}</th>
                <th className="is-num" style={{ width: 128 }}>{t('createAgent.colResolved', 'Résolus · {{days}} j', { days: windowDays })}</th>
                <th className="is-num" style={{ width: 116 }}>{t('createAgent.colMood', 'Humeur · {{days}} j', { days: windowDays })}</th>
                <th className="is-num" style={{ width: 164 }} title={t('createAgent.colAiHint', 'Part des brouillons IA envoyés tels quels par cet employé')}>
                  {t('createAgent.colAi', 'Validation IA · {{days}} j', { days: windowDays })}
                </th>
                <th style={{ width: 92 }} aria-label={t('teamStatsUi.actions', 'Actions')} />
              </tr>
            </thead>
            <tbody>
              {agents.map((agent) => {
                const row = rosterByEmployee[agent._id] || {};
                const name = agent.name || agent.email;
                return (
                  <tr key={agent._id}>
                    <td>
                      <div className="team-v2__identity">
                        <span className="u-avatar u-avatar--sm" aria-hidden="true">{initialsOf(name)}</span>
                        <div className="team-v2__identity-text">
                          <b title={name}>{agent.name}</b>
                          <span className="u-cell-sub" title={agent.email}>{agent.email}</span>
                        </div>
                      </div>
                    </td>
                    <td title={agent.description || ''}>
                      {agent.description || <span className="u-muted">—</span>}
                      {agent.external_admin_id && (
                        <span className="u-cell-sub">{t('teamStatsUi.team.externalId', 'ID outil externe : {{id}}', { id: agent.external_admin_id })}</span>
                      )}
                    </td>
                    <td>
                      {hasStats ? (
                        <span className="team-v2__load">
                          <span><b className="u-num">{formatNumber(row.open_assigned ?? 0)}</b> {t('createAgent.loadOpen', 'ouverts')}</span>
                          {row.stale_open > 0 ? (
                            <Badge tone="warning" dot title={t('teamStatsUi.team.staleHint', 'sans activité depuis plus de {{days}} jours', { days: staleDays })}>
                              {t('teamStatsUi.team.staleBadge', '{{count}} > {{days}} j', { count: row.stale_open, days: staleDays })}
                            </Badge>
                          ) : (
                            <Badge tone="success" dot>{t('createAgent.loadFresh', 'à jour')}</Badge>
                          )}
                        </span>
                      ) : roster === undefined ? <span className="u-skeleton team-v2__cell-skeleton" aria-hidden="true" /> : <span className="u-muted">—</span>}
                    </td>
                    <td className="is-num">
                      {hasStats ? (
                        <>
                          <b className="u-num">{formatNumber(row.resolved_window ?? 0)}</b>
                          {row.resolution_rate_window != null && (
                            <span className="u-cell-sub">{t('teamStatsUi.team.rateSub', '{{rate}} % des reçus', { rate: row.resolution_rate_window })}</span>
                          )}
                        </>
                      ) : <span className="u-muted">—</span>}
                    </td>
                    <td className="is-num">
                      {hasStats && row.avg_sentiment_window != null ? (
                        <span className="team-v2__mood">
                          <span className={`team-v2__dot team-v2__dot--${moodTone(row.avg_sentiment_window)}`} aria-hidden="true" />
                          <span className="u-num">{Math.round(row.avg_sentiment_window)}</span>
                        </span>
                      ) : <span className="u-muted">—</span>}
                    </td>
                    <td className="is-num">
                      {hasStats && row.ai_validated_share_window != null ? (
                        <span
                          title={row.ai_total_window > 0
                            ? t('teamStatsUi.team.aiTitle', '{{share}} % de ses {{count}} réponses IA envoyées sans retouche', { share: row.ai_validated_share_window, count: formatNumber(row.ai_total_window) })
                            : undefined}
                        >
                          <b className="u-num">{`${row.ai_validated_share_window} %`}</b>
                          {row.ai_total_window > 0 && (
                            <span className="u-cell-sub">
                              {tp('polishPagesUi.team.aiSub', 'sur {{formatted}} réponse IA', 'sur {{formatted}} réponses IA', { count: row.ai_total_window, formatted: formatNumber(row.ai_total_window) })}
                            </span>
                          )}
                        </span>
                      ) : <span className="u-muted">—</span>}
                    </td>
                    <td className="is-action">
                      <div className="team-v2__actions">
                        {canViewProductivity && (
                          <button
                            type="button"
                            className="u-btn u-btn--ghost u-btn--icon u-btn--sm"
                            onClick={() => openProductivityFor(agent._id)}
                            aria-label={t('polishPagesUi.team.productivityFor', 'Productivité de {{name}}', { name })}
                            title={t('polishPagesUi.team.productivityFor', 'Productivité de {{name}}', { name })}
                          >
                            <ChartLine aria-hidden="true" />
                          </button>
                        )}
                        {canManage && (
                          <OverflowMenu
                            label={t('teamStatsUi.team.rowActions', 'Actions pour {{name}}', { name })}
                            items={[
                              { label: t('createAgent.editShort', 'Modifier'), icon: Pencil, onClick: () => openEdit(agent) },
                              { separator: true },
                              { label: t('teamStatsUi.team.remove', 'Retirer de l’équipe'), icon: UserMinus, danger: true, onClick: () => setToRemove(agent) },
                            ]}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    );
  };

  const renderTeam = () => (agents.length === 0 ? renderTable() : (
    <div className="team-v2__stack">
      <div className="team-v2__toolbar">
        <span className="team-v2__toolbar-label">{t('teamStatsUi.team.windowLabel', 'Résultats sur')}</span>
        <Segmented
          label={t('createAgent.windowLabel', 'Période')}
          value={windowDays}
          onChange={setWindowDays}
          options={ROSTER_WINDOWS.map((days) => ({ value: days, label: t('teamStatsUi.team.windowDays', '{{days}} jours', { days }) }))}
        />
      </div>
      {roster === null && (
        <Banner tone="warning">{t('teamStatsUi.team.rosterError', 'Les chiffres de l’équipe n’ont pas pu être chargés : la liste des membres reste à jour.')}</Banner>
      )}
      {renderAttention()}
      {renderKpis()}
      {renderTable()}
    </div>
  ));

  const renderForm = () => {
    if (!form) return null;
    const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));
    const creating = form.mode === 'create';
    return (
      <Modal
        icon={creating ? UserPlus : Pencil}
        title={creating
          ? t('teamStatsUi.team.add', 'Ajouter un membre')
          : t('teamStatsUi.team.editTitle', 'Modifier {{name}}', { name: form.agent.name || form.agent.email })}
        description={creating
          ? t('teamStatsUi.team.addDesc', 'Il reçoit par e-mail ses identifiants pour se connecter à Unanimity.')
          : t('teamStatsUi.team.editDesc', 'Un changement d’adresse change aussi l’identifiant de connexion du membre.')}
        onClose={() => !saving && setForm(null)}
        closeLabel={t('common.close', 'Fermer')}
        footer={(
          <>
            <button type="button" className="u-btn u-btn--secondary" onClick={() => setForm(null)} disabled={saving}>
              {t('createAgent.cancel', 'Annuler')}
            </button>
            <button type="submit" form="team-v2-form" className="u-btn u-btn--primary" disabled={saving}>
              {creating ? t('teamStatsUi.team.addSubmit', 'Ajouter et envoyer l’invitation') : t('teamStatsUi.team.save', 'Enregistrer')}
            </button>
          </>
        )}
      >
        <form id="team-v2-form" className="u-form" onSubmit={submitForm}>
          <div className="u-form-grid">
            <Field label={`${t('createAgent.name', 'Nom')} *`} htmlFor="team-v2-name">
              <input id="team-v2-name" className="u-input" type="text" value={form.name} onChange={set('name')} required placeholder={t('teamStatsUi.team.namePlaceholder', 'Prénom Nom')} />
            </Field>
            <Field label={`${t('createAgent.email', 'E-mail')} *`} htmlFor="team-v2-email">
              <input id="team-v2-email" className="u-input" type="text" value={form.email} onChange={set('email')} required placeholder={t('teamStatsUi.team.emailPlaceholder', 'nom@entreprise.fr')} />
            </Field>
          </div>
          <Field
            label={`${t('teamStatsUi.team.roleLabel', 'Rôle et périmètre')} *`}
            htmlFor="team-v2-description"
            help={t('teamStatsUi.team.roleHelp', 'Ce qu’il traite (litiges transporteurs, clients B2B, retours…) : l’agent IA s’en sert pour lui assigner les bons tickets.')}
          >
            <textarea id="team-v2-description" className="u-textarea" rows={3} value={form.description} onChange={set('description')} required />
          </Field>
          {showsExternalId(agents, form.agent) && (
            <Field
              label={t('teamStatsUi.team.externalLabel', 'Identifiant dans votre outil de tickets')}
              htmlFor="team-v2-external"
              help={t('teamStatsUi.team.externalHelp', 'Facultatif. Son identifiant d’administrateur dans votre outil externe (ERP) : un ticket qui lui est assigné y est réassigné aussi.')}
            >
              <input id="team-v2-external" className="u-input" type="text" value={form.externalAdminId} onChange={set('externalAdminId')} placeholder={t('teamStatsUi.team.externalPlaceholder', 'ex. 3530')} />
            </Field>
          )}
          {formError && <Banner tone="danger">{formError}</Banner>}
        </form>
      </Modal>
    );
  };

  const tabs = canManage && canViewProductivity ? (
    <Tabs
      label={t('employeesPage.navLabel', 'Employés')}
      active={view}
      onChange={(key) => updateParams({ view: key === 'team' ? null : key, employee: null })}
      tabs={[
        { key: 'team', label: t('createAgent.tabTeam', 'Équipe'), icon: Users, count: agents.length },
        { key: 'productivity', label: t('createAgent.tabProductivity', 'Productivité'), icon: ChartLine },
      ]}
    />
  ) : null;

  return (
    <div className="u-page team-v2">
      {header}
      <div className="u-well">
        {tabs}
        <div role={tabs ? 'tabpanel' : undefined}>
          {view === 'productivity' && canViewProductivity ? (
            <EmployeeProductivity
              embedded
              initialEmployeeId={productivityEmployeeId}
              onEmployeeChange={(id) => updateParams({ employee: id })}
            />
          ) : renderTeam()}
        </div>
      </div>

      {renderForm()}

      {toRemove && (
        <ConfirmDialog
          icon={UserMinus}
          title={t('teamStatsUi.team.removeTitle', 'Retirer {{name}} de l’équipe ?', { name: toRemove.name || toRemove.email })}
          message={t('teamStatsUi.team.removeMessage', '{{email}} ne pourra plus se connecter à Unanimity. Les tickets qui lui sont assignés le restent : réassignez-les si besoin.', { email: toRemove.email })}
          confirmLabel={t('teamStatsUi.team.removeConfirm', 'Retirer')}
          cancelLabel={t('createAgent.cancel', 'Annuler')}
          onConfirm={confirmRemove}
          onCancel={() => !removing && setToRemove(null)}
          busy={removing}
        />
      )}

      {toast && (
        <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={t('common.close', 'Fermer')}>
          {toast.text}
        </Toast>
      )}
    </div>
  );
};

export default CreateAgent;
